import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import type {
  DropdownAnchorType,
  IMenuCarmiData,
  IMenuCssVars,
  IMenuDefinition,
  IMenuMapperProps,
  IMenuStateRefs,
  ItemTextAlignmentType,
  MenuAnimationNameType,
  MenuDisplayModeType,
  MenuJustificationType,
  MenuOrientationType,
  MenuOverflowType,
  MenuVerticalDropdownDisplayType,
} from '../Menu.types';
import {
  displayModeToCssVarsMap,
  orientationToCssVarsMap,
  dropdownDisplayTypeToCssVarMap,
  verticalOverflowCssVars,
  horizontalOverflowToCssVarsMap,
  justificationToCssVarsMap,
  itemTextAlignmentToCssVarsMap,
  animationNameToCssVarsMap,
  getDirectionCssVars,
  hamburgerMenuAnimationNameToCssVarsMap,
} from './utils/MapperUtils';
import type { IMenuSDKData } from '@wix/editor-elements-corvid-utils/src/props-factories/menuUtils';
import {
  MenuOverflow,
  MenuOrientation,
  VerticalDropdownDisplay,
  HamburgerMenuAnimationName,
  MenuDisplayMode,
} from '../constants';
import {
  ANIMATION_PACKAGE_CONFIG,
  ARIA_LABEL_NAMESPACE,
  translationsKeys,
  defaultTranslations,
} from './constants';
import { withSiteDirection } from '@wix/editor-elements-common-utils';

export const props = withCompInfo<
  IMenuMapperProps,
  IMenuDefinition,
  IMenuCarmiData
>()(
  ['compData', 'translate', 'styleProperties'],
  ({ compData, translate, styleProperties }, carmiData) => {
    return {
      ...carmiData,
      ...compData,
      dropdownAnchor:
        (styleProperties['dropdown-anchor'] as DropdownAnchorType) ||
        'menuItem',
      translations: {
        menuNavAriaLabel:
          translate(ARIA_LABEL_NAMESPACE, translationsKeys.menuNavAriaLabel) ||
          defaultTranslations.menuNavAriaLabel,
        dropdownButtonAriaLabel:
          translate(
            ARIA_LABEL_NAMESPACE,
            translationsKeys.dropdownButtonAriaLabel,
          ) || defaultTranslations.dropdownButtonAriaLabel,
      },
    };
  },
);

export const css = withCompInfo<IMenuCssVars, IMenuDefinition>()(
  ['compData', 'styleProperties'],
  ({ compData, styleProperties }) => {
    const {
      direction,
      submenuDirection = 'inherit',
      itemDirection = 'inherit',
      submenuItemDirection = 'inherit',
      submenuSubItemDirection = 'inherit',
    } = compData;
    const {
      orientation = MenuOrientation.Horizontal,
      'vertical-dropdown-display':
        verticalDropdownDisplay = VerticalDropdownDisplay.AlwaysOpen,
      overflow = MenuOverflow.Scroll,
      'scroll-button-icon-display': scrollButtonIconDisplay,
      'display-mode': displayMode = MenuDisplayMode.Navbar,
      'menu-items-justification': justification = 'none',
      'item-text-align': itemTextAlign = 'center',
      'animation-name': animationName,
      'horizontal-item-icon-display': horizontalItemIconDisplay,
    } = styleProperties;

    const menuAnimatioPackage = animationName as MenuAnimationNameType;

    const hamburgerMenuAnimationName = menuAnimatioPackage
      ? ANIMATION_PACKAGE_CONFIG[menuAnimatioPackage].hamburgerMenuAnimationName
      : HamburgerMenuAnimationName.None;

    const isVerticalOrientation = orientation === MenuOrientation.Vertical;
    const isScrollOverflow = overflow === MenuOverflow.Scroll;

    return {
      '--direction': withSiteDirection(direction),
      '--item-direction': itemDirection,
      '--dropdown-menu-direction': submenuDirection,
      '--dropdown-menu-item-direction': submenuItemDirection,
      '--dropdown-menu-sub-item-direction': submenuSubItemDirection,
      '--sr-only-horizontal-item-icon-display':
        horizontalItemIconDisplay === 'none' ? 'unset' : 'none',
      ...getDirectionCssVars(direction),
      ...displayModeToCssVarsMap[displayMode as MenuDisplayModeType],
      ...orientationToCssVarsMap[orientation as MenuOrientationType],
      ...justificationToCssVarsMap[justification as MenuJustificationType],
      ...itemTextAlignmentToCssVarsMap[itemTextAlign as ItemTextAlignmentType],
      ...animationNameToCssVarsMap[menuAnimatioPackage],
      ...hamburgerMenuAnimationNameToCssVarsMap[hamburgerMenuAnimationName],
      ...(isVerticalOrientation
        ? {
            '--scroll-button-icon-display': 'none',
            ...verticalOverflowCssVars,
            ...dropdownDisplayTypeToCssVarMap[
              verticalDropdownDisplay as MenuVerticalDropdownDisplayType
            ],
          }
        : {
            ...horizontalOverflowToCssVarsMap[overflow as MenuOverflowType],
            ...(isScrollOverflow && scrollButtonIconDisplay !== undefined
              ? {
                  '--scroll-button-icon-display': scrollButtonIconDisplay,
                }
              : {}),
          }),
    };
  },
);

export const sdkData = withCompInfo<IMenuSDKData, IMenuDefinition>()(
  ['pagesMap'],
  ({ pagesMap }) => {
    return {
      pageList: pagesMap,
    };
  },
);

export const stateRefs = withStateRefsValues<keyof IMenuStateRefs>([
  'currentUrl',
  'experiments',
]);

export default createComponentMapperModel({
  css,
  props,
  stateRefs,
  sdkData,
});
